import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock, MapPin, Factory, Building2, Hammer, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import SEO, { newsArticleSchema } from "@/components/SEO";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1562237553-36ad661d6f2c?w=1600&q=80";

const formatDateTime = (iso) =>
  new Date(iso).toLocaleString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const estimateReadMinutes = (content = "") =>
  Math.max(2, Math.round(content.split(/\s+/).length / 180));

const RelatedLink = ({ icon: Icon, overline, title, to, testId }) => (
  <Link
    to={to}
    className="bb-card flex items-center gap-4 p-5 group"
    data-testid={testId}
  >
    <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
      <Icon size={18} strokeWidth={1.8} />
    </span>
    <div className="flex-1 min-w-0">
      <div className="bb-overline text-bb-copper">{overline}</div>
      <div className="font-archivo font-semibold text-bb-navy truncate">
        {title}
      </div>
    </div>
    <ArrowUpRight
      size={16}
      className="text-bb-navy group-hover:text-bb-copper transition-colors"
    />
  </Link>
);

const RelatedArticleCard = ({ article }) => {
  const minutes = estimateReadMinutes(article.content);
  return (
    <article
      className="bb-card group flex flex-col"
      data-testid={`related-article-${article.slug}`}
    >
      <Link
        to={`/news/${article.slug}`}
        className="overflow-hidden border-b border-border"
      >
        <img
          src={article.featuredImage || FALLBACK_IMG}
          alt={article.title}
          className="bb-img w-full aspect-[16/10] object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      </Link>
      <div className="p-5 flex flex-col gap-2 flex-1">
        <span className="bb-overline text-bb-copper">{article.category}</span>
        <h4 className="font-archivo text-base lg:text-lg text-bb-navy font-semibold leading-snug tracking-tight">
          <Link to={`/news/${article.slug}`} className="bb-link">
            {article.title}
          </Link>
        </h4>
        <div className="mt-auto pt-3 border-t border-border flex items-center gap-2 text-[11px] font-mono text-bb-slate">
          <Clock size={11} /> {minutes} min read
        </div>
      </div>
    </article>
  );
};

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [region, setRegion] = useState(null);
  const [commodity, setCommodity] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    setArticle(null);
    setProject(null);
    setCompany(null);
    setRegion(null);
    setCommodity(null);

    api
      .get(`/articles/${slug}`)
      .then(async (r) => {
        if (!mounted) return;
        const a = r.data;
        setArticle(a);

        // Fetch all reference collections once; look up by id locally.
        const [allArticlesRes, projectsRes, companiesRes, regionsRes, commoditiesRes] =
          await Promise.all([
            api.get("/articles"),
            api.get("/projects"),
            api.get("/companies"),
            api.get("/regions"),
            api.get("/commodities"),
          ]);
        if (!mounted) return;

        const allArticles = Array.isArray(allArticlesRes?.data)
          ? allArticlesRes.data
          : [];
        const findById = (list, id) =>
          (Array.isArray(list) ? list : []).find((x) => x.id === id) || null;

        setRelated(
          allArticles
            .filter((x) => x.slug !== a.slug && x.category === a.category)
            .slice(0, 3),
        );
        if (a.project) setProject(findById(projectsRes.data, a.project));
        if (a.company) setCompany(findById(companiesRes.data, a.company));
        if (a.region) setRegion(findById(regionsRes.data, a.region));
        if (a.commodity)
          setCommodity(findById(commoditiesRes.data, a.commodity));
      })
      .catch(() => mounted && setError(true))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug]);

  const paragraphs = useMemo(
    () => (article?.content || "").split(/\n+/).filter(Boolean),
    [article],
  );

  if (loading) {
    return (
      <div
        className="bb-container py-20 text-center bb-overline text-bb-slate"
        data-testid="article-loading"
      >
        Loading dispatch…
      </div>
    );
  }

  if (error || !article) {
    return (
      <section className="bb-container py-20" data-testid="article-not-found">
        <div className="border border-border bg-white p-12 text-center max-w-2xl mx-auto">
          <AlertCircle className="text-bb-copper mx-auto" size={28} />
          <div className="bb-overline text-bb-copper mt-4">404 · Article</div>
          <h1 className="font-archivo text-3xl text-bb-navy mt-3 font-bold">
            We couldn&rsquo;t find that dispatch.
          </h1>
          <p className="text-sm text-bb-slate mt-3">
            It may have been retracted, renamed, or the link is incomplete.
          </p>
          <Link
            to="/news"
            className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
            data-testid="article-back-to-news"
          >
            <ArrowLeft size={14} /> Back to the brief
          </Link>
        </div>
      </section>
    );
  }

  const minutes = estimateReadMinutes(article.content);

  return (
    <article data-testid={`article-detail-${article.slug}`}>
      <SEO
        title={article.title}
        description={article.excerpt}
        image={article.featuredImage}
        type="article"
        jsonLd={newsArticleSchema(article)}
      />
      {/* Hero */}
      <header className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <Link
            to="/news"
            className="bb-link inline-flex items-center gap-2 text-xs font-archivo uppercase tracking-widest text-bb-navy mb-8"
            data-testid="article-back-link"
          >
            <ArrowLeft size={14} /> Back to News
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 flex flex-col justify-end">
              <div className="bb-overline text-bb-copper">
                {article.category}
                {region ? ` · ${region.name}` : ""}
              </div>
              <h1
                className="bb-headline mt-5 text-3xl sm:text-4xl lg:text-5xl text-bb-navy"
                data-testid="article-title"
              >
                {article.title}
              </h1>
              <p className="mt-6 text-base lg:text-lg text-bb-slate leading-relaxed max-w-2xl">
                {article.excerpt}
              </p>
              <div className="mt-8 flex items-center gap-6 flex-wrap text-xs font-mono text-bb-slate">
                <span data-testid="article-author">By {article.author}</span>
                <span className="opacity-40">·</span>
                <span data-testid="article-published">
                  {formatDateTime(article.publishedDate)}
                </span>
                <span className="opacity-40">·</span>
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} /> {minutes} min read
                </span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-border overflow-hidden">
                <img
                  src={article.featuredImage || FALLBACK_IMG}
                  alt={article.title}
                  className="bb-img w-full aspect-[4/3] object-cover"
                  data-testid="article-featured-image"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body + Side rail */}
      <section className="py-14 lg:py-20">
        <div className="bb-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Body */}
            <div
              className="lg:col-span-8"
              data-testid="article-content"
            >
              {paragraphs.map((p, i) => (
                <p
                  key={i}
                  className={`text-bb-slate leading-relaxed ${
                    i === 0
                      ? "text-lg lg:text-xl first-letter:font-archivo first-letter:font-bold first-letter:text-bb-navy first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.95] first-letter:mt-1"
                      : "text-base mt-5"
                  }`}
                >
                  {p}
                </p>
              ))}

              <div className="mt-12 pt-6 border-t border-border flex items-center justify-between flex-wrap gap-3">
                <span className="bb-overline text-bb-slate">
                  Filed by {article.author}
                </span>
                <Link
                  to="/news"
                  className="bb-link inline-flex items-center gap-1 text-xs font-archivo uppercase tracking-widest text-bb-navy"
                  data-testid="article-footer-back"
                >
                  More from the desk
                  <ArrowUpRight size={14} strokeWidth={2.4} />
                </Link>
              </div>
            </div>

            {/* Side rail — cross-links */}
            <aside className="lg:col-span-4 space-y-3" data-testid="article-cross-links">
              <div className="bb-overline text-bb-slate mb-3">In this story</div>
              {project && (
                <RelatedLink
                  icon={Hammer}
                  overline="Project"
                  title={project.name}
                  to={`/projects/${project.slug}`}
                  testId="cross-link-project"
                />
              )}
              {company && (
                <RelatedLink
                  icon={Building2}
                  overline="Company"
                  title={company.name}
                  to={`/companies/${company.slug}`}
                  testId="cross-link-company"
                />
              )}
              {region && (
                <RelatedLink
                  icon={MapPin}
                  overline="Region"
                  title={region.name}
                  to={`/regions/${region.slug}`}
                  testId="cross-link-region"
                />
              )}
              {commodity && (
                <RelatedLink
                  icon={Factory}
                  overline="Commodity"
                  title={commodity.name}
                  to={`/commodities/${commodity.slug}`}
                  testId="cross-link-commodity"
                />
              )}
              {!project && !company && !region && !commodity && (
                <div className="border border-dashed border-border bg-bb-offwhite p-5 text-sm text-bb-slate font-mono">
                  No cross-links flagged on this dispatch.
                </div>
              )}

              <div className="pt-6 mt-6 border-t border-border">
                <Link
                  to="/#newsletter"
                  className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm w-full"
                  data-testid="article-newsletter-cta"
                >
                  Get the Morning Brief
                  <ArrowUpRight size={14} strokeWidth={2.4} />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section
          className="py-14 lg:py-20 border-t border-border bg-white"
          data-testid="article-related-section"
        >
          <div className="bb-container">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="bb-overline text-bb-copper">Related</div>
                <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 font-bold tracking-tight">
                  More from the {article.category} desk.
                </h2>
              </div>
              <Link
                to={`/news?category=${encodeURIComponent(article.category)}`}
                className="bb-link text-xs font-archivo uppercase tracking-widest text-bb-navy inline-flex items-center gap-1"
                data-testid="article-related-view-all"
              >
                View all {article.category}
                <ArrowUpRight size={14} strokeWidth={2.4} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <RelatedArticleCard key={r.id} article={r} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
